import { AlertTriangle, CheckCircle2, Clock, HelpCircle, RefreshCw, ShieldAlert, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  accountTone, channelTone, stamp, worstDestination,
} from "./jobDiagnosticsDisplay";

// Typed backend codes -> the sentence an operator can act on.
const WAITING_TEXT = {
  CLIENT_NOT_INITIALIZED: "the Telegram client is still starting up",
  CLIENT_DISCONNECTED: "the account is reconnecting",
  CLIENT_RECOVERY_FAILED: "the client could not be rebuilt",
  NETWORK_ERROR: "the network is unavailable",
  TELEGRAM_RPC_ERROR: "Telegram returned an error",
  ACCOUNT_RECORD_NOT_FOUND: "the assigned account no longer exists",
  SESSION_NOT_FOUND: "the account has no saved session",
  SESSION_UNAUTHORIZED: "the session was revoked",
  ACCOUNT_DISABLED: "the account is paused",
};

// One place that turns backend state into operator-readable words. The whole point
// of this panel is that nobody should have to open a log file to learn why a job is
// not copying, so every branch here ends in a sentence, never a code.
const TONE = {
  ok: { color: "var(--c-emerald)", Icon: CheckCircle2 },
  warn: { color: "var(--c-amber)", Icon: Clock },
  bad: { color: "var(--c-rose)", Icon: XCircle },
  unknown: { color: "var(--tg-faint)", Icon: HelpCircle },
};

const Line = ({ label, tone, text, title }) => {
  const { color, Icon } = TONE[tone] || TONE.unknown;
  return (
    <div className="flex items-start gap-2 min-w-0" title={title || text}>
      <Icon className="h-3.5 w-3.5 shrink-0 mt-[1px]" style={{ color }} />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-[var(--tg-faint)]">{label}</div>
        <div className="text-[11px] truncate" style={{ color }}>{text}</div>
      </div>
    </div>
  );
};

export const JobDiagnostics = ({ job, onValidate, onRepickDestination, validating }) => {
  const d = job.diagnostics;
  if (!d) return null;

  const validation = d.last_validation;
  const src = accountTone(d.source_account);
  const dst = accountTone(d.dest_account);
  const sameAccount = d.source_account?.account_id === d.dest_account?.account_id;
  const sourceCh = channelTone(d.source, "source", validation);
  // A job can fan out to several destinations; surface the worst one, since that is
  // the one actually stopping the job.
  const destCh = channelTone(worstDestination(d.destinations, validation),
    "destination", validation);

  const lastCopy = stamp(d.last_successful_copy);
  const blocked = d.destination_blocked;
  // A job that has since validated READY and is running again should not wear a red
  // banner just because an old error is still recorded on it. Only surface the error
  // while it still describes the job's current state.
  const errorIsCurrent = d.last_error
    && (job.status === "error" || job.status === "failed")
    && validation?.state !== "READY";
  const showProblem = blocked || errorIsCurrent
    || validation?.state === "VALIDATION_FAILED";
  // The live peer cache is empty after a restart, so `blocked` alone would hide the
  // corrective action on a job that is still known-bad from its persisted verdict.
  const needsRepick = blocked
    || validation?.code === "DESTINATION_NOT_ACCESSIBLE";

  return (
    <div
      className="mt-3 pt-3 border-t border-[var(--tg-border)]"
      data-testid={`job-diagnostics-${job.id}`}
    >
      <div className="grid gap-x-5 gap-y-2.5 sm:grid-cols-2 lg:grid-cols-3">
        <Line label="Source account" tone={src.tone} text={src.text} />
        {!sameAccount && <Line label="Destination account" tone={dst.tone} text={dst.text} />}
        <Line label="Source channel" tone={sourceCh.tone} text={sourceCh.text} title={sourceCh.title} />
        <Line label="Destination channel" tone={destCh.tone} text={destCh.text} title={destCh.title} />
        <Line
          label="Last successful copy"
          tone={lastCopy ? "ok" : "unknown"}
          text={lastCopy || "None recorded yet"}
        />
        <Line
          label="Runtime state"
          tone={d.waiting_reason ? "warn" : (d.source_account?.recovering || d.dest_account?.recovering) ? "warn" : "ok"}
          text={
            d.waiting_reason
              ? `Waiting — ${WAITING_TEXT[d.waiting_reason] || d.waiting_reason}`
              : (d.source_account?.recovering || d.dest_account?.recovering)
                ? "Account reconnecting — the job resumes automatically"
                : "Nothing blocking"
          }
        />
        {typeof d.source_account?.reconnect_count === "number" && (
          <Line
            label="Recovery"
            tone={d.source_account.recovering ? "warn" : "ok"}
            text={`${d.source_account.reconnect_count} reconnect(s) since this account was added`}
          />
        )}
        {validation && (
          <Line
            label="Last validation"
            tone={validation.state === "READY" ? "ok" : "bad"}
            title={validation.reason || undefined}
            text={`${validation.state === "READY" ? "READY" : "VALIDATION FAILED"}${stamp(validation.checked_at) ? ` · ${stamp(validation.checked_at)}` : ""}`}
          />
        )}
      </div>

      {showProblem && (
        <div
          className="mt-3 flex items-start gap-2 rounded-md px-3 py-2 text-[11px]"
          style={{ color: "var(--c-rose)", background: "rgb(var(--c-rose-rgb)/0.08)" }}
          data-testid={`job-problem-${job.id}`}
        >
          {blocked ? <ShieldAlert className="h-3.5 w-3.5 shrink-0 mt-[1px]" />
            : <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-[1px]" />}
          <span className="min-w-0">
            {d.destination_blocked_reason || validation?.reason || d.last_error}
          </span>
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          disabled={validating}
          onClick={() => onValidate(job.id)}
          className="border-[var(--tg-border)] hover:bg-[var(--tg-hover)] h-7 text-[11px]"
          data-testid={`validate-job-${job.id}`}
          title="Check account, session, client, source, destination and posting rights. Copies nothing."
        >
          <RefreshCw className={`h-3 w-3 mr-1.5 ${validating ? "animate-spin" : ""}`} />
          {validating ? "Validating…" : "Validate job"}
        </Button>
        {needsRepick && (
          <Button
            size="sm"
            onClick={() => onRepickDestination(job)}
            className="h-7 text-[11px] bg-[rgb(var(--c-rose-rgb)/0.15)] text-[var(--c-rose)] hover:bg-[rgb(var(--c-rose-rgb)/0.25)] border border-[rgb(var(--c-rose-rgb)/0.30)]"
            data-testid={`repick-destination-${job.id}`}
          >
            Re-pick destination
          </Button>
        )}
      </div>
    </div>
  );
};
